import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, ChatMessage, ChatSession, VideoContext } from '@/types';

// ==================== Auth Store ====================

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  setAuth: (user: User, token: string) => void;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      setAuth: (user, token) => {
        localStorage.setItem('token', token);
        set({ user, token, isAuthenticated: true });
      },
      clearAuth: () => {
        localStorage.removeItem('token');
        set({ user: null, token: null, isAuthenticated: false });
      },
    }),
    {
      name: 'auth-storage',
    }
  )
);

// ==================== Chat Store ====================

interface ChatState {
  sessions: ChatSession[];
  currentSessionId: string | null;
  isLoading: boolean;
  messages: ChatMessage[];
  
  // 操作
  createSession: () => string;
  setCurrentSession: (id: string) => void;
  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>, sessionId?: string | null) => void;
  updateLastMessage: (content: string, data?: unknown, type?: ChatMessage['type'], sessionId?: string | null) => void;
  setLoading: (loading: boolean) => void;
  clearMessages: () => void;
  deleteSession: (id: string) => void;
  updateSessionTitle: (id: string, title: string) => void;
  setSessionVideoContext: (id: string, context: VideoContext) => void;
}

const generateId = () => Math.random().toString(36).substring(2, 15);

/**
 * 根据视频上下文生成会话标题，以视频主旨为主要依据
 */
function generateTitleFromVideoContext(context: VideoContext): string {
  const parts: string[] = [];
  if (context.category && context.category !== '未识别') {
    parts.push(context.category);
  }
  if (context.contentType && context.contentType !== '未识别') {
    parts.push(context.contentType);
  }
  if (parts.length > 0) {
    return parts.join(' · ');
  }
  if (context.summary) {
    const detail = context.summary.slice(0, 16);
    const suffix = context.summary.length > 16 ? '…' : '';
    return `${detail}${suffix}`;
  }
  return '视频解析';
}

/**
 * 根据消息内容生成会话标题（无视频上下文时兜底使用）
 */
function generateSessionTitle(content: string): string {
  const trimmed = content.trim();
  if (!trimmed) return '新会话';

  // 去掉快捷操作前缀【xxx】，提取后面的实质内容
  const cleaned = trimmed.replace(/^【.+?】\s*/, '').trim();
  const effective = cleaned || trimmed;

  // 如果包含抖音链接，视为视频解析
  if (/https?:\/\/(v\.douyin\.com|www\.douyin\.com)/.test(effective)) {
    const rest = effective.replace(/https?:\/\/\S+/g, '').trim();
    if (rest) {
      const detail = rest.slice(0, 10);
      const suffix = rest.length > 10 ? '…' : '';
      return `视频解析：${detail}${suffix}`;
    }
    return '视频解析';
  }

  // 根据关键词判断方向
  const directionKeywords: { keywords: string[]; prefix: string }[] = [
    { keywords: ['评论', '热评', '口碑'], prefix: '评论' },
    { keywords: ['用户', '账号', '同类', '粉丝'], prefix: '用户' },
    { keywords: ['视频', '抖音', '解析', '链接'], prefix: '视频' },
    { keywords: ['搜索', '查找', '热门', '品类'], prefix: '探索' },
    { keywords: ['分析', '统计', '数据'], prefix: '分析' },
  ];

  let prefix = '';
  for (const item of directionKeywords) {
    if (item.keywords.some((kw) => effective.includes(kw))) {
      prefix = item.prefix;
      break;
    }
  }

  const body = effective.replace(/https?:\/\/\S+/g, '').trim();
  const source = body || effective;
  const detail = source.slice(0, prefix ? 10 : 12);
  const suffix = source.length > (prefix ? 10 : 12) ? '…' : '';

  return prefix ? `${prefix}：${detail}${suffix}` : `${detail}${suffix}`;
}

export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
      sessions: [],
      currentSessionId: null,
      isLoading: false,
      messages: [],

      createSession: () => {
        const newSession: ChatSession = {
          id: generateId(),
          title: '新会话',
          messages: [],
          created_at: Date.now(),
          updated_at: Date.now(),
        };
        set((state) => ({
          sessions: [newSession, ...state.sessions],
          currentSessionId: newSession.id,
          messages: [],
          isLoading: false,
        }));
        return newSession.id;
      },

      setCurrentSession: (id) => {
        const session = get().sessions.find((s) => s.id === id);
        set({
          currentSessionId: id,
          messages: session?.messages || [],
        });
      },

      addMessage: (message, sessionId?) => {
        const safeContent = typeof message.content === 'string' ? message.content : String(message.content || '');
        const newMessage: ChatMessage = {
          ...message,
          content: safeContent,
          id: generateId(),
          timestamp: Date.now(),
        };
        set((state) => {
          const targetId = sessionId ?? state.currentSessionId;
          const targetSession = state.sessions.find((s) => s.id === targetId);

          // 更新目标会话的消息列表
          const targetMessages = targetSession ? [...targetSession.messages, newMessage] : [...state.messages, newMessage];

          // 如果当前会话标题还是默认值，且新增的是用户消息，自动生成标题
          let titleUpdate = {};
          if (
            targetSession &&
            targetSession.title === '新会话' &&
            newMessage.role === 'user'
          ) {
            // 如果用户发的是【解析视频】且还没拿到解析结果，先不命名，
            // 等异步解析完成后由 setSessionVideoContext 以视频主旨重命名
            const isParseVideoRequest = /^【解析视频】/.test(safeContent);
            if (!isParseVideoRequest) {
              // 优先以解析出的视频主旨命名；没有视频上下文则兜底按消息内容
              const newTitle = targetSession.videoContext
                ? generateTitleFromVideoContext(targetSession.videoContext)
                : generateSessionTitle(safeContent);
              titleUpdate = { title: newTitle };
            }
          }

          // 同时更新会话列表中的消息
          const updatedSessions = state.sessions.map((s) =>
            s.id === targetId
              ? { ...s, messages: targetMessages, updated_at: Date.now(), ...titleUpdate }
              : s
          );

          // 如果当前显示的就是目标会话，也更新 state.messages
          const messagesUpdate = state.currentSessionId === targetId
            ? { messages: targetMessages }
            : {};

          return { sessions: updatedSessions, ...messagesUpdate };
        });
      },

      updateLastMessage: (content, data, type, sessionId?) => {
        const safeContent = typeof content === 'string' ? content : String(content || '');
        console.log('[Store] updateLastMessage called with content:', safeContent.slice(0, 50));
        set((state) => {
          const targetId = sessionId ?? state.currentSessionId;
          const targetSession = state.sessions.find((s) => s.id === targetId);
          if (!targetSession) {
            console.log('[Store] Aborting: target session not found');
            return state;
          }

          const targetMessages = targetSession.messages;
          const lastMessage = targetMessages[targetMessages.length - 1];
          console.log('[Store] lastMessage:', lastMessage);
          if (!lastMessage || lastMessage.role !== 'assistant') {
            console.log('[Store] Aborting: last message is not assistant');
            return state;
          }
          
          const updatedMessages = [...targetMessages.slice(0, -1), {
            ...lastMessage,
            content: safeContent,
            data,
            type: type || lastMessage.type,
          }];
          
          const updatedSessions = state.sessions.map((s) =>
            s.id === targetId
              ? { ...s, messages: updatedMessages, updated_at: Date.now() }
              : s
          );
          
          // 如果当前显示的就是目标会话，也更新 state.messages
          const messagesUpdate = state.currentSessionId === targetId
            ? { messages: updatedMessages }
            : {};

          return { sessions: updatedSessions, ...messagesUpdate };
        });
      },

      setLoading: (loading) => set({ isLoading: loading }),

      clearMessages: () => set({ messages: [] }),

      deleteSession: (id) => {
        set((state) => {
          const newSessions = state.sessions.filter((s) => s.id !== id);
          const newCurrentId =
            state.currentSessionId === id
              ? newSessions[0]?.id || null
              : state.currentSessionId;
          return {
            sessions: newSessions,
            currentSessionId: newCurrentId,
            messages: newSessions.find((s) => s.id === newCurrentId)?.messages || [],
          };
        });
      },

      updateSessionTitle: (id, title) => {
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === id ? { ...s, title } : s
          ),
        }));
      },

      setSessionVideoContext: (id, context) => {
        set((state) => ({
          sessions: state.sessions.map((s) => {
            if (s.id !== id) return s;
            const updated: ChatSession = { ...s, videoContext: context };
            // 如果标题还是默认值，立即以视频主旨重命名
            if (s.title === '新会话') {
              updated.title = generateTitleFromVideoContext(context);
            }
            return updated;
          }),
        }));
      },
    }),
    {
      name: 'chat-storage',
      partialize: (state) => {
        // isLoading 是临时 UI 状态，不应持久化
        const { isLoading, ...rest } = state;
        return { ...rest, isLoading: false };
      },
    }
  )
);

// ==================== UI Store ====================

interface UIState {
  sidebarOpen: boolean;
  theme: 'light' | 'dark';
  toggleSidebar: () => void;
  setTheme: (theme: 'light' | 'dark') => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      sidebarOpen: true,
      theme: 'light',
      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      setTheme: (theme) => set({ theme }),
    }),
    {
      name: 'ui-storage',
    }
  )
);
